import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRightIcon, CheckIcon, ClockIcon, DollarSignIcon, GraduationCapIcon, LayersIcon, ShieldCheckIcon } from 'lucide-react';
import { usePageMeta } from '../hooks/usePageMeta';
import { LegalFooter } from '../components/LegalFooter';
import { buildFindDegreesClickout } from '../services/mediaMatchers/clickout';
import { getAreaHeroImage, getRelatedLevels, getRelatedPrograms, type DegreeProgramCombo } from '../utils/degreeSlugs';
interface DegreeProgramLandingPageProps {
  combo: DegreeProgramCombo;
}

/**
 * Generated landing page for one degree level × program combination.
 *
 * The copy is assembled from the combination itself rather than hand-written,
 * so every program has a page without claiming specifics — no invented tuition
 * figures, salaries, school names, or program lengths.
 */
export function DegreeProgramLandingPage({
  combo
}: DegreeProgramLandingPageProps) {
  const location = useLocation();
  const heroImage = getAreaHeroImage(combo.areaStudy);
  usePageMeta(`Online ${combo.title} Programs | Online College Guide`, `Compare accredited online ${combo.programLabel} programs. Get matched by career goals, schedule, and budget.`);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [combo.slug]);
  const ctaHref = buildFindDegreesClickout(location.search, {
    degreeInterest: combo.degreeInterest,
    areaStudy: combo.areaStudy,
    mProgram: combo.mProgram
  });
  const relatedLevels = getRelatedLevels(combo);
  const relatedPrograms = getRelatedPrograms(combo);
  const reasons = [{
    icon: ShieldCheckIcon,
    title: 'Accredited schools only',
    body: `Every ${combo.programLabel} program we match you with comes from an accredited institution, so your credits and credential hold their value.`
  }, {
    icon: ClockIcon,
    title: 'Built around your schedule',
    body: 'Online coursework lets you keep working while you study, with start dates throughout the year rather than one intake each fall.'
  }, {
    icon: DollarSignIcon,
    title: 'Aid you may qualify for',
    body: 'Accredited schools eligible for federal aid can accept the FAFSA. Ask each school what applies to your situation once you are matched.'
  }];
  return <main className="bg-white" data-id="element-1453" data-mp="tjszz82henxl">
      {/* Top bar */}
      <div className="bg-white border-b border-gray-100" data-id="element-1454" data-mp="5t5pcf16lk7c">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between" data-id="element-1455" data-mp="5rkvysqls7b2">
          <Link to="/" className="flex items-center" data-id="element-1456" data-mp="shshcq9u957">
            <img src="https://cdn.magicpatterns.com/uploads/hM6pwxGTxH7R3Ug6xJ6sm8/Horizontal_OCG_logo.png" alt="Online College Guide - Your Path Forward" className="h-8 md:h-10 w-auto object-contain" data-id="element-1457" data-mp="fkjup3ftde8e" />
          </Link>
          <div className="hidden sm:flex items-center text-xs font-semibold text-gray-500 tracking-wider uppercase" data-id="element-1458" data-mp="3e9qufkqozt56">
            <ShieldCheckIcon className="w-4 h-4 mr-1.5 text-green-600" data-id="element-1459" data-mp="1tr6kw3g8ptx9" />
            Trusted by 2M+ Students
          </div>
        </div>
      </div>

      {/* Hero — photography of the work this field leads to, behind a navy
          scrim so the headline stays legible over the image. */}
      <header className={heroImage ? 'relative bg-navy' : 'hero-bg'} style={heroImage ? {
      backgroundImage: `linear-gradient(to right, rgba(10,31,68,0.94), rgba(10,31,68,0.80)), url(${heroImage})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center right'
    } : undefined} data-id="element-1460" data-mp="1orlifj1v33fs">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20" data-id="element-1461" data-mp="27i379cjkpevv">
          <nav aria-label="Breadcrumb" className="mb-6" data-id="element-1462" data-mp="s2cga5d43acm">
            <ol className="flex flex-wrap items-center gap-2 text-xs text-white/70" data-id="element-1463" data-mp="s0t16i36z5cf">
              <li data-id="element-1464" data-mp="w595wg12k6q2">
                <Link to="/" className="hover:text-white transition-colors" data-id="element-1465" data-mp="w1lbzfrppiat">
                  Home
                </Link>
              </li>
              <li aria-hidden="true" data-id="element-1466" data-mp="17jlw2qthrcja">/</li>
              <li data-id="element-1467" data-mp="17isyoggf9zdt">
                <Link to="/find-degrees" className="hover:text-white transition-colors" data-id="element-1468" data-mp="17iat2t7ve8t8">
                  Find Degrees
                </Link>
              </li>
              <li aria-hidden="true" data-id="element-1469" data-mp="1v9ojuuqdhwc3">/</li>
              <li className="text-white/90" data-id="element-1470" data-mp="1v8dit2obb2oo">{combo.areaLabel}</li>
            </ol>
          </nav>

          {/* On narrow screens the headline leads and the CTA follows it, so
              the visitor knows what the page is before being asked to act.
              On desktop the CTA returns to the right-hand column. */}
          <div className="grid lg:grid-cols-5 lg:grid-rows-[auto_1fr] gap-y-8 lg:gap-y-0 gap-x-10 lg:gap-x-14 items-start" data-id="element-1471" data-mp="1h9nn9rh55b2f">
            <div className="order-1 lg:col-start-1 lg:col-span-3 lg:row-start-1" data-id="element-1472" data-mp="5urwj757zj5i">
              <p className="text-[11px] font-semibold uppercase tracking-widest text-gold mb-4" data-id="element-1473" data-mp="5wuy8ofqj0q2">
                {combo.degreeLabel} · {combo.areaLabel}
              </p>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight lg:mb-5" data-id="element-1474" data-mp="2lkicgzd4gr1w">
                Online {combo.title} Programs
              </h1>
            </div>

            <div className="order-3 lg:col-start-1 lg:col-span-3 lg:row-start-2" data-id="element-1475" data-mp="2vajxtje3bvd4">
              <p className="text-lg text-white/80 leading-relaxed max-w-2xl mb-8" data-id="element-1476" data-mp="309egmlc25jx8">
                Compare accredited online {combo.programLabel.toLowerCase()}{' '}
                programs at the {combo.degreeLabel.toLowerCase()} level. Tell us
                your goals and we'll connect you with schools that fit your
                schedule and budget — at no cost, with no obligation.
              </p>
              <ul className="flex flex-wrap gap-x-6 gap-y-3 text-sm text-white/80" data-id="element-1477" data-mp="1ewbqgw1w5sm2">
                {['Accredited', 'Flexible online study', 'No obligation'].map(item => <li key={item} className="inline-flex items-center gap-2" data-id="element-1478" data-mp="2n6ahzmpehafg">
                      <CheckIcon className="w-4 h-4 text-gold flex-shrink-0" aria-hidden="true" data-id="element-1479" data-mp="2i6pi9a2eoy1o" />
                      {item}
                    </li>)}
              </ul>
            </div>

            {/* CTA card */}
            <div className="order-2 lg:col-start-4 lg:col-span-2 lg:row-start-1 lg:row-span-2 bg-white rounded-2xl shadow-2xl ring-4 ring-white/20 p-8 sm:p-9 text-center" data-id="element-1480" data-mp="3633ctne7ujbw">
              <GraduationCapIcon className="w-11 h-11 text-navy mx-auto mb-5" aria-hidden="true" data-id="element-1481" data-mp="3ew6uy8zt3kcv" />
              <h2 className="text-2xl font-extrabold text-navy leading-tight mb-3" data-id="element-1482" data-mp="3avd8lk20idm3">
                Find {combo.programLabel} Colleges
              </h2>
              <p className="text-base text-gray-600 leading-relaxed mb-7" data-id="element-1483" data-mp="3jw1okkayuo1m">
                We'll show you accredited schools offering{' '}
                {combo.programLabel.toLowerCase()} programs.
              </p>
              <a href={ctaHref} className="group w-full inline-flex items-center justify-center gap-2 bg-cta hover:bg-cta-dark text-white font-extrabold py-5 px-6 rounded-xl text-lg shadow-lg hover:shadow-xl transition-[background-color,box-shadow] duration-200 focus:outline-none focus:ring-4 focus:ring-cta/40" data-id="element-1484" data-mp="cba5fqu9jn09">
                Get Matched Now
                <ArrowRightIcon className="w-6 h-6 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" data-id="element-1485" data-mp="3phse7n83lzhi" />
              </a>
              <p className="mt-5 text-sm text-gray-500" data-id="element-1486" data-mp="3fqxvgsu29i0t">
                Takes about 2 minutes
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Why online */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20" data-id="element-1487" data-mp="35tt4mfp9sydo">
        <h2 className="text-2xl sm:text-3xl font-bold text-navy mb-3 text-center" data-id="element-1488" data-mp="3m9lthlcsm97r">
          Why study {combo.programLabel.toLowerCase()} online?
        </h2>
        <p className="text-gray-600 max-w-2xl mx-auto mb-10 leading-relaxed text-center" data-id="element-1489" data-mp="3h7wg3z23ljgi">
          An online {combo.degreeLabel.toLowerCase()} lets you build
          credentials in {combo.areaLabel.toLowerCase()} without leaving your
          job. What matters to employers is the school's accreditation and the
          skills you gain, not whether you attended in person.
        </p>
        <div className="grid md:grid-cols-3 gap-8" data-id="element-1490" data-mp="2yi6m3e65t1pn">
          {reasons.map(reason => <div key={reason.title} className="flex flex-col items-center text-center" data-id="element-1491" data-mp="2tkwmx6syxbom">
              <reason.icon className="w-6 h-6 text-crimson mb-4" aria-hidden="true" data-id="element-1492" data-mp="2ommqytzf671y" />
              <h3 className="text-base font-bold text-navy mb-2" data-id="element-1493" data-mp="2jobm6xnzemql">
                {reason.title}
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed" data-id="element-1494" data-mp="v2l4u4arqoa3">
                {reason.body}
              </p>
            </div>)}
        </div>
      </section>

      {/* Related */}
      {(relatedLevels.length > 0 || relatedPrograms.length > 0) && <section className="bg-light border-y border-gray-200" data-id="element-1495" data-mp="1442htdsvq9uk">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20 space-y-12" data-id="element-1496" data-mp="192m3re7h3lmx">
            {relatedLevels.length > 0 && <div data-id="element-1497" data-mp="bgez64b65tak">
                <h2 className="text-xl sm:text-2xl font-bold text-navy mb-1.5" data-id="element-1498" data-mp="gdonthqqi0xc">
                  {combo.programLabel} at other levels
                </h2>
                <p className="text-sm text-gray-600 mb-6" data-id="element-1499" data-mp="lc0162g8mtkb">
                  Same field, different credential. Compare how far each level
                  takes you.
                </p>
                <ul className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-1500" data-mp="3osgvlyriv1zu">
                  {relatedLevels.map(item => <li key={item.slug} data-id="element-1501" data-mp="3tqqqe89c40kx">
                      <Link to={`/find-degrees/${item.slug}`} className="group flex flex-col h-full bg-white border border-gray-200 rounded-xl p-5 hover:border-navy hover:shadow-md transition-[border-color,box-shadow] duration-200 focus:outline-none focus:ring-2 focus:ring-crimson" data-id="element-1502" data-mp="22jr5j5a38v9n">
                        <span className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-crimson mb-2" data-id="element-1503" data-mp="2hg04e5q2h6lj">
                          <LayersIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-1504" data-mp="1os40afy64ceq" />
                          {item.degreeLabel}
                        </span>
                        <span className="text-base font-semibold text-navy leading-snug mb-4" data-id="element-1505" data-mp="1tpmccf7hf7qy">
                          {item.programLabel}
                        </span>
                        <span className="mt-auto inline-flex items-center gap-1.5 text-sm font-semibold text-navy" data-id="element-1506" data-mp="113b7gscuekoc">
                          View programs
                          <ArrowRightIcon className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" data-id="element-1507" data-mp="15z9uabiivumn" />
                        </span>
                      </Link>
                    </li>)}
                </ul>
              </div>}
            {relatedPrograms.length > 0 && <div data-id="element-1508" data-mp="30djukyiwakfr">
                <h2 className="text-xl sm:text-2xl font-bold text-navy mb-1.5" data-id="element-1509" data-mp="30e1vw9kezouj">
                  Other {combo.areaLabel.toLowerCase()} programs
                </h2>
                <p className="text-sm text-gray-600 mb-6" data-id="element-1510" data-mp="2rkgj2vrt8hnr">
                  Still deciding? These {combo.degreeLabel.toLowerCase()}{' '}
                  programs sit in the same field.
                </p>
                <ul className="flex flex-wrap gap-2.5" data-id="element-1511" data-mp="2hn23szhmhyx1">
                  {relatedPrograms.map(item => <li key={item.slug} data-id="element-1512" data-mp="2cn5x2shjjtds">
                      <Link to={`/find-degrees/${item.slug}`} className="group inline-flex items-center gap-2 bg-white border border-gray-200 rounded-full pl-4 pr-3 py-2.5 text-sm font-medium text-navy hover:border-navy hover:shadow-sm transition-[border-color,box-shadow] duration-200 focus:outline-none focus:ring-2 focus:ring-crimson" data-id="element-1513" data-mp="2co7k7vbu06bv">
                        {item.programLabel}
                        <ArrowRightIcon className="w-3.5 h-3.5 text-gray-400 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-navy" aria-hidden="true" data-id="element-1514" data-mp="2lq1k1jinivlf" />
                      </Link>
                    </li>)}
                </ul>
              </div>}
          </div>
        </section>}

      {/* Closing CTA */}
      <section className="bg-navy" data-id="element-1515" data-mp="34n2qz9vrn5s0">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center" data-id="element-1516" data-mp="39gfflk0g3ckr">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4" data-id="element-1517" data-mp="y0x092e48jew">
            Ready to compare {combo.programLabel.toLowerCase()} programs?
          </h2>
          <p className="text-white/80 leading-relaxed mb-8" data-id="element-1518" data-mp="t1am8qqgqz51">
            Answer a few questions and we'll match you with accredited schools
            offering online {combo.degreeLabel.toLowerCase()} programs. No cost
            to students, with no obligation to enroll.
          </p>
          <a href={ctaHref} className="group inline-flex items-center justify-center gap-2 bg-cta hover:bg-cta-dark text-white font-bold py-4 px-10 rounded-lg text-base transition-colors duration-200 shadow-lg" data-id="element-1519" data-mp="f2uagldquig1">
            Get Matched Now
            <ArrowRightIcon className="w-5 h-5 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" data-id="element-1520" data-mp="k0vzdmxupbzf" />
          </a>
          <p className="mt-5 text-xs text-white/50 uppercase tracking-widest" data-id="element-1521" data-mp="1vgklkc3a66z6">
            No cost · No obligation · Takes 2 minutes
          </p>
        </div>
      </section>

      <LegalFooter data-id="element-1522" data-mp="51jsl4nl4tqf" />
    </main>;
}